import { Link } from "react-router-dom";
import { ShieldOff } from "lucide-react";
import { useSuplantacion } from "@/lib/sesion";

/**
 * Pantalla única de «acceso no autorizado».
 *
 * Es el mismo mensaje en todas las secciones: antes cada guard dibujaba el suyo
 * y decían cosas distintas para lo mismo. Aquí no se consulta nada al backend
 * —esa es justamente la idea: cuando la sesión no tiene la facultad, no se
 * lanza la petición que iba a terminar en 403—, así que sirve tanto para un
 * usuario que llegó por un enlace ajeno como para una suplantación en curso.
 *
 * Durante una suplantación se explica de quién es la sesión: sin eso, un
 * administrador leería «tu cuenta no tiene permiso» y pensaría que el sistema
 * está roto, cuando lo correcto es que esté viendo lo que ve el suplantado.
 */
export function SinAcceso({ roles }: { roles: string[] }) {
  const { suplantacion } = useSuplantacion();
  const quien = suplantacion.suplantadoNombre ?? suplantacion.suplantadoCorreo;

  return (
    <div className="mx-auto max-w-xl rounded-xl border border-edge/70 bg-surface p-6 shadow-card">
      <div className="flex items-start gap-3">
        <ShieldOff className="mt-0.5 h-5 w-5 shrink-0 text-estado-warn" />
        <div className="min-w-0 space-y-2">
          <h2 className="text-base font-semibold text-fg">Esta sección no está disponible</h2>

          {suplantacion.activa ? (
            <p className="text-sm text-fg-muted">
              Estás navegando como <strong>{quien}</strong>, y esa cuenta no tiene acceso a esta
              sección. Es el comportamiento correcto: estás viendo SICEN tal como lo ve esa persona.
              Sal de la suplantación con el botón de la banda superior para volver a lo tuyo.
            </p>
          ) : (
            <p className="text-sm text-fg-muted">
              Tu cuenta no tiene los permisos necesarios para entrar aquí. Si necesitas acceso,
              solicítalo al administrador de SICEN.
            </p>
          )}

          <p className="text-xs text-fg-subtle">
            Requiere {roles.length === 1 ? "el rol" : "alguno de los roles"}:{" "}
            {roles.map((r, i) => (
              <span key={r}>
                {i > 0 && ", "}
                <code>{r}</code>
              </span>
            ))}
            .
          </p>

          <Link
            to="/"
            className="inline-flex items-center text-sm font-medium text-primary-strong hover:underline"
          >
            Volver al inicio
          </Link>
        </div>
      </div>
    </div>
  );
}
